import { useEffect, useState } from "react";
import { useChangeBooleans, useComponentProperties, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import usePages, { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { ATTRIBUTE_LABEL, ATTRIBUTE_PLURAL_LABEL } from "../../Helpers/Labels/AttributeLabel";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ENTITY_LABEL } from "../../Helpers/Labels/EntityLabel";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import { getPageByProperty } from "../utils";

function SplitView() {

    const RelativeGroupSize = ["30%", "50%"];
    const RelativeSpecificSize = ["70%", "50%"];

    const formProperties = componentPropertiesMap.SplitView;

    // hooks
    const [{ pages }] = usePages();
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetContext = useResetComponentProperties();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");

    const [properties, setProperty] = useComponentProperties();
    const [{ isGeneralPropertiesChanged }, setChangeProperty] = useChangeBooleans();

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            setProperties();
            setChangeProperty("isGeneralPropertiesChanged", false);
        }
    }, [selectedPage, selectedComponent])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    const [tabSelected, setSelectedTab] = useState(0);

    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
        }
        const allPages = pages.map(folder => folder.items).flat();
        const listPage = getPageByProperty(allPages, "_id", selectedComponent.groupPageId);
        const detailsPage = getPageByProperty(allPages, "_id", selectedComponent.specificPageId);
        setProperty("groupPage", listPage);
        setProperty("specificPage", detailsPage);
    }

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function handleSectionSize(event) {
        handleInputChange(event);
        const otherField = event.target.name === "leftSectionSize" ? "rightSectionSize" : "leftSectionSize";
        switch (event.target.value) {
            case "30%":
                setProperty(otherField, "70%");
                break;
            case "50%":
                setProperty(otherField, "50%");
                break;
            case "70%":
                setProperty(otherField, "30%");
                break;
            default:
                break;
        }
    }

    function handleInputChange(event) {
        setProperty(event.target.name, event.target.value);
    }

    function swapPages() {
        const tempGroupPage = properties.groupPage;
        const tempSpecificPage = properties.specificPage;
        setProperty("groupPage", tempSpecificPage);
        setProperty("specificPage", tempGroupPage);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined &&
                selectedComponent[property] !== properties[property]
            ) {
                isChanged = true;
            }
        }
        if (
            (
                properties.groupPage &&
                selectedComponent.groupPageId !== properties.groupPage._id
            ) ||
            (
                properties.specificPage &&
                selectedComponent.specificPageId !== properties.specificPage._id
            )
        ) {
            isChanged = true;
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    function getPageOfSubPageType(subPageType) {
        const groupPages = [];
        for (const folder of pages) {
            for (const page of folder.items) {
                if (page.subPageType === subPageType && page.portalId === selectedPage.portalId) {
                    groupPages.push(page)
                }
            }
        }
        return groupPages;
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title="General">
                        <Scrollable>
                            <form className="p-2">
                                <div className="row">
                                    <div className="col-3">
                                        <button
                                            disabled={selectedComponent.hidden}
                                            onClick={swapPages}
                                            type="button"
                                            className="btn btn-default"
                                        >
                                            <i className="fad fa-sync"></i>
                                        </button>
                                    </div>
                                    <div className="col-3">Type</div>
                                    <div className="col-3">Relative Size</div>
                                    <div className="col-3">Reference Page</div>
                                </div>
                                <div className="mt-2 row">
                                    <div className="col-3">{properties.leftLabel}</div>
                                    <div className="col-3">Group</div>
                                    <div className="col-3">
                                        <DropDownList
                                            name="leftSectionSize"
                                            onChange={handleSectionSize}
                                            disabled={selectedComponent.hidden}
                                            className="form-control-custom w-100"
                                            value={properties.leftSectionSize}
                                            data={RelativeGroupSize}
                                        />
                                    </div>
                                    <div className="col-3">
                                        <DropDownList
                                            name="groupPage"
                                            onChange={handleInputChange}
                                            disabled={selectedComponent.hidden}
                                            className="form-control-custom w-100"
                                            value={properties.groupPage}
                                            data={getPageOfSubPageType("List")}
                                            textField="text"
                                            dataItemKey="_id"
                                        />
                                    </div>
                                </div>
                                <div className="mt-2 row">
                                    <div className="col-3">{properties.rightLabel}</div>
                                    <div className="col-3">Specific</div>
                                    <div className="col-3">
                                        <DropDownList
                                            name="rightSectionSize"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleSectionSize}
                                            className="form-control-custom w-100"
                                            value={properties.rightSectionSize}
                                            data={RelativeSpecificSize}
                                        />
                                    </div>
                                    <div className="col-3">
                                        <DropDownList
                                            name="specificPage"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom w-100"
                                            value={properties.specificPage}
                                            data={getPageOfSubPageType("Single Record")}
                                            textField="text"
                                            dataItemKey="_id"
                                        />
                                    </div>
                                </div>
                            </form>
                        </Scrollable>
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default SplitView;